import { Button } from "./button";
import { cn } from "@/lib/utils";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "./pagination";

interface ListPaginationProps {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number | 'all';
  totalItems: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (items: number | 'all') => void;
  className?: string;
}

export function ListPagination({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  className,
}: ListPaginationProps) {
  const showPagination = itemsPerPage !== 'all' && totalPages > 1;
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const maxPagesToShow = 7;

    if (totalPages <= maxPagesToShow) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push('ellipsis');
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('ellipsis');
      }

      // Show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const startItem = itemsPerPage === 'all' ? 1 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = itemsPerPage === 'all' ? totalItems : Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className={cn("flex flex-row flex-wrap items-center justify-between gap-2 sm:gap-4 mt-4 print:hidden", className)}>
      {/* Items per page selector */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Show:</span>
        <div className="flex gap-1">
          {[10, 25, 50].map((option) => (
            <button
              key={option}
              onClick={() => onItemsPerPageChange(option)}
              className={cn(
                "px-3 py-1.5 rounded text-xs font-medium transition-colors",
                itemsPerPage === option
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted"
              )}
            >
              {option}
            </button>
          ))}
          <button
            onClick={() => onItemsPerPageChange('all')}
            className={cn(
              "px-3 py-1.5 rounded text-xs font-medium transition-colors",
              itemsPerPage === 'all'
                ? "bg-primary text-primary-foreground"
                : "hover:bg-muted"
            )}
          >
            All
          </button>
        </div>
        <span className="ml-2">
          ({startItem}-{endItem} of {totalItems})
        </span>
      </div>

      {/* Page navigation */}
      {showPagination && (
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={!canGoPrevious}
              className="gap-1"
            >
              <span className="sr-only">Previous</span>
              <span aria-hidden>Previous</span>
            </Button>
          </PaginationItem>

          {getPageNumbers().map((page, index) => (
            <PaginationItem key={index}>
              {page === 'ellipsis' ? (
                <PaginationEllipsis />
              ) : (
                <PaginationLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onPageChange(page);
                  }}
                  isActive={page === currentPage}
                >
                  {page}
                </PaginationLink>
              )}
            </PaginationItem>
          ))}

          <PaginationItem>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={!canGoNext}
              className="gap-1"
            >
              <span className="sr-only">Next</span>
              <span aria-hidden>Next</span>
            </Button>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
      )}
    </div>
  );
}
